import React from 'react'
import ReactDOM from 'react-dom'

import { named, withDependencies, optional } from '@wix/thunderbolt-ioc'
import type {
	ExternalComponentSiteConfig,
	IExternalComponentLoader,
	IExternalComponentWixCodeUrlGenerator,
	IExternalComponentConsentManager,
} from '../types'
import {
	name,
	ExternalComponentWixCodeUrlGeneratorSymbol,
	ExternalComponentConsentManagerSymbol,
	ExternalComponentFetchSymbol,
	ExternalComponentCssManagerSymbol,
} from '../symbols'
import {
	SiteFeatureConfigSymbol,
	LoggerSymbol,
	ILogger,
	BrowserWindowSymbol,
	BrowserWindow,
	ExperimentsSymbol,
	Experiments,
} from '@wix/thunderbolt-symbols'
import { BYOC_ROOT_CLASS } from './externalComponentCssManager'
import {
	ExternalComponentMissingExportError,
	ExternalComponentWrongExportError,
	ExternalComponentMissingConsent,
	ExternalComponentReloadingError,
	clientExternals,
} from '@wix/external-component-renderer'
import { amdLoaderFactory } from '@wix/thunderbolt-renderer-utils'
import { prepareTrustedComponent } from '../components/TrustedComponent'
import { ExternalComponentFetchApi, IExternalComponentCssManager } from '../types'
import SSRFallback from '../components/SSRFallback'

type ModuleEntries = { [namedExport: string]: React.ComponentType<any> }
type Modules = Map<
	string,
	{
		entries?: Promise<ModuleEntries>
		cacheKillerCounter: number
	}
>

export const ExternalComponentLoader = withDependencies(
	[
		named(SiteFeatureConfigSymbol, name),
		LoggerSymbol,
		ExternalComponentFetchSymbol,
		ExternalComponentWixCodeUrlGeneratorSymbol,
		ExternalComponentCssManagerSymbol,
		optional(ExternalComponentConsentManagerSymbol),
		BrowserWindowSymbol,
		ExperimentsSymbol,
	],
	(
		externalComponentSiteConfig: ExternalComponentSiteConfig,
		logger: ILogger,
		fetchApi: ExternalComponentFetchApi,
		externalComponentWixCodeUrlGenerator: IExternalComponentWixCodeUrlGenerator,
		externalComponentCssManager: IExternalComponentCssManager,
		externalComponentConsentManager: IExternalComponentConsentManager,
		window: BrowserWindow,
		experiments: Experiments
	): IExternalComponentLoader => {
		const amdLoader = amdLoaderFactory({
			scriptsCache: {},
			withFetchRetry: false,
			fetch: fetchApi,
			globalThis,
			defaultDependencies: {
				react: React,
				React,
				'react-dom': ReactDOM,
				...clientExternals,
			},
		})
		const modules: Modules = new Map()

		const getModuleEntries = async (fullUrl: string, modulePath: string) => {
			await window?.externalsRegistry.react.loaded
			return amdLoader.loadModule<ModuleEntries>(fullUrl).then((moduleEntries) => {
				if (Object.keys(moduleEntries).length === 0) {
					throw new ExternalComponentMissingExportError(modulePath)
				}
				return moduleEntries
			})
		}

		const loadComponent = async ({
			url,
			appDefId,
			importKey = 'default',
			onError,
		}: {
			url: string
			appDefId?: string
			importKey: string
			onError: (error: any) => void
		}) => {
			if (!modules.has(url)) {
				modules.set(url, {
					cacheKillerCounter: 0,
				})
			}
			const module = modules.get(url)!
			if (!module.entries) {
				const fullUrl = externalComponentWixCodeUrlGenerator.generateUrl({
					componentRelativeUrl: url,
					cacheKillerCounter: module.cacheKillerCounter > 0 ? module.cacheKillerCounter : undefined,
					appDefId,
				})
				module.entries = getModuleEntries(fullUrl, url)
			}

			const Component = await module.entries!.then((moduleEntries) => {
				if (!moduleEntries[importKey]) {
					throw new ExternalComponentWrongExportError(importKey, url)
				}

				return prepareTrustedComponent(moduleEntries[importKey], onError, experiments)
			})

			return Component
		}

		const loadComponentCss = async (compId: string, url: string, contextId: string) => {
			const customParams = {
				compId,
				moduleUrl: url,
			}
			logger.interactionStarted('byoc-load-css', { customParams, eventGuid: customParams.compId })
			await externalComponentCssManager.use(contextId, url)
			logger.interactionEnded('byoc-load-css', { customParams, eventGuid: customParams.compId })
		}

		return {
			invalidateFileCache(url: string) {
				if (!modules.has(url)) {
					return
				}
				const module = modules.get(url)
				module!.entries = undefined
				module!.cacheKillerCounter++
			},

			invalidateAllCache() {
				for (const url of modules.keys()) {
					this.invalidateFileCache(url)
				}
			},

			async loadComponentAndUpdateProps({
				comp,
				compData,
				contextId,
				showLoader = false,
				failedOnSsr = false,
				isRetry = false,
			}) {
				const interactionName = isRetry ? 'byoc-load-component-retry' : 'byoc-load-component'
				const { disableBYOC } = externalComponentSiteConfig
				const nullifyComponentAndReport = (e: Error) => {
					comp.updateProps({ Component: null, error: e })
					logger.captureError(e, {
						tags: {
							feature: 'viewer-external-component',
							error: e.message,
							type: e.name,
						},
					})
				}

				type LoadInteractionCustomParam = {
					moduleUrl: string
					importKey: string
					compId: string
					loadPrevented: boolean
					loadPreventedReason: 'failedOnSsr' | 'forceDisable' | 'missingImportData' | 'userConsent' | ''
					fetchFailed: boolean
				}

				const reportLoadPreventedBi = (
					customParams: LoadInteractionCustomParam,
					reason: LoadInteractionCustomParam['loadPreventedReason']
				) => {
					const newCustomParams = { ...customParams, loadPrevented: true, loadPreventedReason: reason }
					logger.interactionStarted(interactionName, {
						customParams: newCustomParams,
						eventGuid: customParams.compId,
					})
					logger.interactionEnded(interactionName, {
						customParams: newCustomParams,
						eventGuid: customParams.compId,
					})
				}

				const { url, importKey, appDefId } = compData

				const customParams: LoadInteractionCustomParam = {
					moduleUrl: url,
					importKey,
					compId: comp.id,
					loadPrevented: false,
					loadPreventedReason: '',
					fetchFailed: false,
				}

				if (failedOnSsr) {
					comp.updateProps({ Component: SSRFallback })
					reportLoadPreventedBi(customParams, 'failedOnSsr')
					return
				}

				const userConsent = externalComponentConsentManager ? await externalComponentConsentManager.get() : true

				if (!userConsent) {
					comp.updateProps({ Component: null, error: new ExternalComponentMissingConsent() })
					reportLoadPreventedBi(customParams, 'userConsent')
					return
				}

				if (disableBYOC || !url || !importKey) {
					comp.updateProps({ Component: null })
					const reason = disableBYOC ? 'forceDisable' : 'missingImportData'
					reportLoadPreventedBi(customParams, reason)
					return
				}
				if (showLoader) {
					comp.updateProps({ Component: null, error: new ExternalComponentReloadingError() })
				}

				const cssReadyPromise = loadComponentCss(comp.id, compData.url, contextId)

				try {
					logger.interactionStarted(interactionName, { customParams, eventGuid: customParams.compId })
					const Component = await loadComponent({
						url,
						importKey,
						onError: nullifyComponentAndReport,
						appDefId,
					})

					logger.interactionEnded(interactionName, { customParams, eventGuid: customParams.compId })

					if (cssReadyPromise) {
						await cssReadyPromise
					}
					comp.updateProps({ Component, className: BYOC_ROOT_CLASS, error: undefined })
				} catch (e) {
					logger.interactionEnded(interactionName, {
						customParams: {
							...customParams,
							fetchFailed: true,
						},
						eventGuid: customParams.compId,
					})
					nullifyComponentAndReport(e)
				}
			},
		}
	}
)
